import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { BookingForm } from "@/components/BookingForm";
import { MapBlock } from "@/components/MapBlock";
import { Reveal } from "@/components/Reveal";
import { getPublicSettings } from "@/lib/api";
import { SALON, IMAGES } from "@/data/content";

const REVIEWS = [
  { name: "Nazar Melnichuk", text: "Ein wirklich toller Friseursalon! Ich komme jeden Monat hierher und bin jedes Mal sehr zufrieden mit dem Ergebnis. Das Team ist professionell, freundlich und geht immer auf meine Wünsche ein. Die Atmosphäre ist angenehm, der Service hervorragend und der Haarschnitt immer top. Es ist schön, einen Friseur zu haben, bei dem man weiß, dass man in guten Händen ist. Absolut empfehlenswert!" },
  { name: "Eric Biermann", text: "Gehe schon sehr lange dort hin. Sehr liebenswert, schnelle Terminvereinbarung und sehr guter Haarschnitt" },
  { name: "Relono", text: "Ich bin hier sehr zufrieden mit Farbe und Schnitt. Danke Nes" },
  { name: "Heinz Eilfgang", text: "Toller Friseursalon. Total nettes Team. Immer gute, positive und nette Stimmung. Ergebnis immer top. Meistens komme ich auch ohne Termin dran." },
  { name: "Petra Oppenberg", text: "Sehr nettes Team! Haarschnitt und Pflege phantastisch, genau nach Wunsch und passend zum Typ. Sehr zu empfehlen!" },
  { name: "Uwe K", text: "Sehr freundlich und zuvorkommend. Bin immer wieder sehr zufrieden." },
  { name: "Winfried Conzen", text: "Kleiner aber feiner Salon mit freundlichen Personal, hervorragende Beratung und Bedienung" },
];

const VORTEILE = [
  { nr: "01 / TEMPO", title: "Schnell dran", text: "Wir planen präzise, damit Sie pünktlich und ohne Hektik auf dem Salonstuhl Platz nehmen." },
  { nr: "02 / SPONTAN", title: "Kurzfristige Termine", text: "Regelmäßig freie Zeitfenster – oft noch in derselben Woche. Einfach anfragen oder anrufen." },
  { nr: "03 / DETAIL", title: "Persönliche Beratung", text: "Ausführlich und ehrlich: Wir finden die Nuance und Schnittlinie, die zu Ihnen passt." },
  { nr: "04 / SERVICE", title: "Top Service", text: "Freundlicher, zuvorkommender Service für Damen, Herren und Kinder – in entspannter Atmosphäre." },
];

const SCHRITTE = [
  { nr: "01", title: "Termin wählen", text: "Wunschtermin online anfragen oder einfach anrufen – auch kurzfristig." },
  { nr: "02", title: "Beratung genießen", text: "Wir nehmen uns Zeit: Was wünschen Sie sich? Was passt zu Ihnen?" },
  { nr: "03", title: "Wohlfühlen", text: "Entspannt zurücklehnen und mit einem Lächeln aus dem Salon gehen." },
];

const BELIEBT = [
  { tag: "Farbe & Veredelung", title: "Balayage & Highlights", text: "Freihand-Farbtechniken, weiche Übergänge und glänzende Glossings für natürliche Tiefenwirkung.", price: "ab 120 €", service: "Balayage" },
  { tag: "Schnitt & Form", title: "Damen, Herren & Kinder", text: "Klassische und moderne Schnitte, Konturen und schonendes Föhnen für jedes Alter.", price: "ab 18 €", service: "Damenhaarschnitt" },
  { tag: "Länge & Form", title: "Dauerwelle & Extensions", text: "Langanhaltende Sprungkraft durch moderne Dauerwelle oder mehr Länge und Fülle mit Echthaar-Extensions.", price: "ab 70 €", service: "Dauerwelle" },
];

export default function Start() {
  const [reviewUrl, setReviewUrl] = useState("");
  useEffect(() => {
    getPublicSettings()
      .then(({ data }) => setReviewUrl(data.google_review_url || ""))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="page-start">
      {/* HERO */}
      <section className="py-16 md:py-24 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex flex-wrap items-center gap-3 mb-6">
              <span className="border border-[#B08D57] text-[#B08D57] px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-sans" data-testid="hero-new-owner-badge">Unter neuer Leitung</span>
              <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] font-sans border-b border-[#B08D57]/40 pb-1">Haarstudio in 50129 Glessen</span>
            </div>
            <h1 className="text-[clamp(40px,5.5vw,88px)] mb-6 leading-none">
              <span className="ln"><span>Zeit für Ihr Haar.</span></span>
              <span className="ln"><span>Persönlich beraten.</span></span>
            </h1>
            <p className="text-lg text-[#24211D]/80 max-w-lg mb-8 nes-fade leading-relaxed" data-testid="hero-subline">
              Entspannte Salon-Atmosphäre, meisterhafte Farbverläufe und verlässliche Termine ohne lange Wartezeiten – auch kurzfristig.
            </p>
            <div className="flex flex-wrap items-center gap-5 nes-fade">
              <a href="#anfrage" data-testid="hero-booking-btn" className="bg-[#B08D57] hover:bg-[#967442] text-[#FBF8F3] px-8 py-4 text-xs uppercase tracking-[0.18em] font-sans transition-[transform,background-color] duration-300 hover:-translate-y-1">
                Termin buchen
              </a>
              <a href={SALON.phoneHref} data-testid="hero-call-btn" className="text-xs uppercase tracking-[0.18em] text-[#24211D]/80 hover:text-[#24211D] underline underline-offset-8 font-sans transition-colors duration-200">
                Jetzt anrufen: {SALON.phoneDisplay}
              </a>
            </div>
            <p className="mt-6 nes-fade">
              <Link to="/karriere" data-testid="hero-career-hint" className="text-[11px] uppercase tracking-[0.15em] text-[#B08D57] hover:text-[#24211D] font-sans transition-colors duration-200 border-b border-[#B08D57]/40 pb-0.5">
                Übrigens: Wir freuen uns über Verstärkung – offene Stellen →
              </Link>
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="nes-wipe">
              <figure className="aspect-[4/5] relative border border-[#24211D]/10 overflow-hidden rounded-t-[12rem] group">
                <img src={IMAGES.storefront} alt="Schaufenster des Haarstudio Nes in Glessen mit Nes-Logo und Pflanze" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="eager" />
                <figcaption className="absolute bottom-4 left-4 text-xs uppercase tracking-[0.14em] text-[#24211D]/85 bg-[#FBF8F3]/90 px-3 py-2 font-sans">Atelier Heidenpfuhl 7</figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* 4 VORTEILE */}
      <section className="bg-[#24211D] text-[#FBF8F3] py-20" data-testid="vorteile-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Die Nes Philosophie</span>
            <h2 className="text-3xl md:text-5xl">Vier Versprechen an Ihren Besuch</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {VORTEILE.map((v, i) => (
              <Reveal key={v.nr} delay={i * 0.08}>
                <div className="border-t border-[#B08D57]/40 pt-6" data-testid={`vorteil-${v.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <span className="text-xs font-display text-[#B08D57] block mb-2">{v.nr}</span>
                  <h3 className="text-xl mb-2">{v.title}</h3>
                  <p className="text-xs text-[#FBF8F3]/70 leading-relaxed font-sans">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BELIEBTESTE LEISTUNGEN */}
      <section className="py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Atelier Portfolio</span>
              <h2 className="text-3xl md:text-5xl">Beliebte Leistungen</h2>
            </div>
            <p className="text-xs text-[#24211D]/70 max-w-xs leading-relaxed font-sans">
              Richtpreise – der verbindliche Preis richtet sich nach Haarlänge und Aufwand und wird in der kostenlosen Beratung genannt.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {BELIEBT.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="border border-[#24211D]/15 bg-[#FFFFFF] p-8 flex flex-col justify-between h-full hover:border-[#B08D57]/50 transition-colors duration-300" data-testid={`service-card-${s.service.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">{s.tag}</span>
                  <h3 className="text-2xl mb-4">{s.title}</h3>
                  <p className="text-xs text-[#24211D]/75 leading-relaxed mb-6 font-sans">{s.text}</p>
                </div>
                <div className="pt-6 border-t border-[#24211D]/10 flex items-center justify-between">
                  <span className="text-sm font-sans font-bold">{s.price}</span>
                  <Link to={`/kontakt?service=${encodeURIComponent(s.service)}#termin`} data-testid={`service-book-${s.service.toLowerCase().replace(/[^a-z]/g, "-")}`} className="text-xs uppercase tracking-widest text-[#B08D57] hover:text-[#24211D] font-sans transition-colors duration-200">
                    Buchen →
                  </Link>
                </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/leistungen" data-testid="all-services-link" className="text-xs uppercase tracking-[0.18em] text-[#24211D]/80 hover:text-[#B08D57] underline underline-offset-8 font-sans transition-colors duration-200">
              Alle Leistungen & Richtpreise ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* SO EINFACH GEHT'S */}
      <section className="bg-[#F4EFE6] py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">So einfach geht's</span>
            <h2 className="text-3xl md:text-5xl">In drei Schritten zum Wohlfühl-Haar</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {SCHRITTE.map((s, i) => (
              <Reveal key={s.nr} delay={i * 0.1}>
                <div className="border-t border-[#B08D57]/40 pt-6" data-testid={`schritt-${s.nr}`}>
                  <span className="font-display text-4xl text-[#B08D57] block mb-3">{s.nr}</span>
                  <h3 className="text-xl mb-2">{s.title}</h3>
                  <p className="text-xs text-[#24211D]/70 leading-relaxed font-sans">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALERIE-VORSCHAU */}
      <section className="py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Vorher / Nachher</span>
              <h2 className="text-3xl md:text-5xl">Einblicke aus dem Atelier</h2>
            </div>
            <Link to="/galerie" data-testid="gallery-preview-link" className="text-xs uppercase tracking-[0.18em] text-[#B08D57] hover:text-[#24211D] font-sans transition-colors duration-200">
              Zur Galerie →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { src: IMAGES.balayageReal, alt: "Echtes Balayage-Ergebnis aus dem Haarstudio Nes" },
              { src: IMAGES.wavesReal, alt: "Echtes Ergebnis: Schnitt und Wellen-Styling aus dem Haarstudio Nes" },
              { src: IMAGES.highlightsReal, alt: "Echte Highlights aus dem Haarstudio Nes" },
            ].map((img, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure className="aspect-[3/4] border border-[#24211D]/10 overflow-hidden" data-testid={`gallery-preview-${i}`}>
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                </figure>
              </Reveal>
            ))}
          </div>
          <p className="text-[11px] text-[#24211D]/50 font-sans mt-4">Echte Ergebnisse aus unserem Studio in Glessen.</p>
        </div>
      </section>

      {/* KUNDENSTIMMEN: echte Google-Rezensionen als Laufband */}
      <section className="bg-[#F4EFE6] py-14 border-b border-[#24211D]/10">
        <div className="text-center" data-testid="reviews-section">
          <div className="max-w-2xl mx-auto px-6">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">{REVIEWS.length} Google-Rezensionen · durchgehend 5 Sterne</span>
            <h2 className="text-2xl md:text-3xl mb-3">Erfahrungen unserer Salon-Gäste</h2>
            <p className="text-xs text-[#24211D]/70 font-sans max-w-xl mx-auto leading-relaxed mb-8" data-testid="reviews-note">
              Nes hat viele Jahre als Mitarbeiterin in diesem Salon gearbeitet, bevor sie ihn am 19. Februar 2026 übernommen hat – diese echten Google-Stimmen erzählen also auch von ihrer Arbeit.
            </p>
          </div>
          <div className="reviews-marquee relative overflow-hidden" data-testid="reviews-marquee">
            <div className="reviews-marquee-track flex items-start w-max py-1">
              {[...REVIEWS, ...REVIEWS].map((r, i) => (
                <figure
                  key={i}
                  aria-hidden={i >= REVIEWS.length}
                  data-testid={i < REVIEWS.length ? `review-card-${i}` : undefined}
                  className="w-[300px] sm:w-[360px] shrink-0 mr-14 border border-[#24211D]/15 bg-[#FFFFFF] p-6 text-left flex flex-col"
                >
                  <div className="flex gap-1 mb-3" aria-label="5 von 5 Sternen">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={14} className="fill-[#B08D57] text-[#B08D57]" />
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed text-[#24211D]/85 flex-1">„{r.text}"</blockquote>
                  <figcaption className="mt-4 pt-3 border-t border-[#24211D]/10">
                    <span className="block text-xs uppercase tracking-[0.15em] font-sans text-[#24211D]">{r.name}</span>
                    <span className="block text-[11px] font-sans text-[#24211D]/50 mt-0.5">Google-Rezension</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
          {reviewUrl && (
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-btn"
              className="mt-6 inline-block bg-[#B08D57] hover:bg-[#967442] text-[#FBF8F3] px-8 py-3.5 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300"
            >
              Bewerte uns auf Google
            </a>
          )}
        </div>
      </section>

      {/* BANNER KURZFRISTIGE TERMINE */}
      <section className="bg-[#B08D57] text-[#FBF8F3] py-14">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-4xl mb-1">Kurzfristig freie Termine</h2>
            <p className="text-sm text-[#FBF8F3]/85 font-sans">Oft noch diese Woche ein Platz frei – jetzt anfragen oder direkt anrufen.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a href="#anfrage" data-testid="banner-booking-btn" className="bg-[#FBF8F3] text-[#24211D] hover:bg-[#FFFFFF] px-8 py-4 text-xs uppercase tracking-[0.18em] font-sans transition-[transform,background-color] duration-300 hover:-translate-y-1">
              Jetzt anfragen
            </a>
            <a href={SALON.phoneHref} data-testid="banner-call-btn" className="border border-[#FBF8F3]/60 text-[#FBF8F3] hover:bg-[#FBF8F3]/10 px-8 py-4 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300">
              {SALON.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* BESUCH: KARTE, ADRESSE, ZEITEN */}
      <section className="py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Ihr Besuch</span>
            <h2 className="text-3xl md:text-5xl mb-6">Mitten in Glessen</h2>
            <p className="text-sm text-[#24211D]/80 leading-relaxed mb-6" data-testid="visit-address">
              {SALON.name}<br />{SALON.street}<br />{SALON.city}<br />
              <a href={SALON.phoneHref} className="text-[#B08D57] hover:text-[#24211D] transition-colors duration-200">{SALON.phoneDisplay}</a>
            </p>
            <div className="border-t border-[#24211D]/10 pt-6" data-testid="visit-hours">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#B08D57] block mb-3 font-sans">Öffnungszeiten</span>
              {SALON.hours.map((h) => (
                <div key={h.days} className="flex justify-between text-sm py-1.5 border-b border-[#24211D]/5">
                  <span className="text-[#24211D]/80">{h.days}</span>
                  <span className="font-sans font-bold text-xs pt-0.5">{h.time}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <MapBlock />
          </div>
        </div>
      </section>

      {/* KARRIERE-TEASER */}
      <section className="bg-[#24211D] text-[#FBF8F3] py-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Wir suchen Verstärkung</span>
            <h2 className="text-3xl md:text-5xl mb-6">Werde Teil vom Haarstudio Nes</h2>
            <p className="text-sm text-[#FBF8F3]/80 leading-relaxed mb-6 font-sans">
              Wir suchen Friseur/in (Voll- oder Teilzeit) sowie Auszubildende. Familiäres Team, geregelte Zeiten ohne Sonntags- und Montagsarbeit, Unterstützung bei Weiterbildungen.
            </p>
            <Link to="/karriere" data-testid="career-teaser-link" className="border border-[#B08D57] text-[#B08D57] hover:bg-[#B08D57] hover:text-[#24211D] px-8 py-3.5 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300 inline-block">
              Zur Karriereseite
            </Link>
          </div>
          <div className="lg:col-span-4 border border-[#FBF8F3]/10 p-6 bg-[#2B2723]">
            <span className="text-xs font-display text-[#B08D57] block mb-3">Ihre Salon-Vorteile</span>
            <ul className="text-xs space-y-2 text-[#FBF8F3]/80 font-sans">
              <li>• Keine Sonntags- & Montagsarbeit</li>
              <li>• Ruhiges, entspanntes Arbeitsklima</li>
              <li>• Unterstützung bei Weiterbildungen</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ANFRAGE-FORMULAR */}
      <section id="anfrage" className="py-20 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Terminvereinbarung</span>
            <h2 className="text-3xl md:text-5xl mb-3">Wunschtermin & Rückruf</h2>
            <p className="text-xs text-[#24211D]/75 font-sans">Senden Sie uns Ihren Wunschzeitraum – wir rufen zur Bestätigung kurz zurück. Auch kurzfristige Termine sind möglich.</p>
          </div>
          <BookingForm />
        </div>
      </section>
    </div>
  );
}
